import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import invariant from 'tiny-invariant'

import ActionButton from '@/components/misc/ActionButton'
import ConfigureEmailsFormModal from '@/components/misc/ReferralLinksTable/components/ConfigureEmails/ConfigureEmailsFormModal'
import { ReferralLinkTableRow } from '@/components/misc/ReferralLinksTable/types'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import useDialog from '@/hooks/useDialog'
import useNotyf from '@/hooks/useNotyf'
import { faEnvelope } from '@/lib/fontAwesomePro/regular'
import { getIdFromIri } from '@/lib/helpers/helperFunctions'
import { QUERY_KEYS } from '@/lib/queryKeys'
import companyUserService from '@/lib/services/companyUserService'
import referralLinkService from '@/lib/services/referralLinkService'

interface Props {
  referralLink: ReferralLinkTableRow
}

const ConfigureEmailsButton: React.FC<Props> = ({ referralLink }) => {
  const currentCompany = useCurrentCompanyOrFail()
  const notyf = useNotyf()
  const queryClient = useQueryClient()
  const { isShowingDialog, showDialog, hideDialog } = useDialog()

  invariant(
    referralLink.fromCompany.id === currentCompany.id,
    'Can only configure emails for links where the current company is the fromCompany',
  )

  const companyUsersQuery = useQuery({
    queryKey: QUERY_KEYS.companyUsers({ companyId: currentCompany.id }),
    queryFn: () =>
      companyUserService.getCompanyUsers({ companyId: currentCompany.id }),
  })

  const updateFromCompanyUsersBccMutation = useMutation({
    mutationFn: (selectedCompanyUserIds: number[]) =>
      referralLinkService.updateFromCompanyUsersBcc(
        referralLink.id,
        selectedCompanyUserIds,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.referralLinks(),
      })
      notyf.success('Changes saved')
      hideDialog()
    },
    onError: () => {
      notyf.error('Failed to update email configuration. Please try again.')
    },
  })

  const shouldShowModal =
    isShowingDialog && !companyUsersQuery.isPending && !!companyUsersQuery.data

  return (
    <>
      <ActionButton
        onClick={showDialog}
        variant="outline-primary"
        size="sm"
        showIsProcessingText={false}
        showSpinner={false}
        isProcessing={companyUsersQuery.isPending}
      >
        <FontAwesomeIcon icon={faEnvelope} />
        Configure emails
      </ActionButton>

      {shouldShowModal && (
        <ConfigureEmailsFormModal
          companyUsers={companyUsersQuery.data['hydra:member']}
          selectedCompanyUserIds={referralLink.fromCompanyUsersToBcc.map(
            (companyUserIri) => getIdFromIri(companyUserIri),
          )}
          onChange={(selectedCompanyUserIds) =>
            updateFromCompanyUsersBccMutation.mutate(selectedCompanyUserIds)
          }
          isMutating={updateFromCompanyUsersBccMutation.isPending}
          onHide={hideDialog}
          toCompanyPresentationName={referralLink.toCompany.presentationName}
        />
      )}
    </>
  )
}

export default ConfigureEmailsButton
